import {IServiceFactory} from "../service-factory.interface";
import {
    IStationConfiguration,
    IStationMarketConfiguration
} from "../configuration/objects/stations-configurations.interface";
import {ICountryConfiguration} from "../configuration/objects/country-configuration.interface";
import {IStation} from "./station.interface";
import {NullableDate, NullableUndefinedString} from "../../types/nullable-types";
import {IPsoMarket} from "./pso-market.interface";

export class Mac {
    constructor(public readonly code: string,
                public readonly name: string,
                public readonly macStations: string[],
                public readonly countryCode: string) {
    }
}

export type NullableMac = Mac | null;

export class Station implements IStation {
    constructor(private readonly services: IServiceFactory,
                private readonly stationConfig: IStationConfiguration,
                private readonly mac: NullableMac,
                public readonly isMac: boolean) {
    }

    get stationCode(): string {
        return this.stationConfig.code;
    }


    get stationMacCode(): string {
        if(this.mac?.code) {
            return this.mac.code;
        }

        return this.stationCode;
    }

    get stationName(): string {
        return this.stationConfig.name;
    }

    get stationMacName(): string {
        if(this.mac?.name) {
            return this.mac.name;
        }

        return this.stationName;
    }

    get marketsConfig(): IStationMarketConfiguration[] {
        return this.stationConfig.markets
    }

    get allMacStations(): string[] {
        return this.mac?.macStations ?? [];
    }

    private get country(): ICountryConfiguration | null {
        const country = this.services.country.tryGetCountry(this.stationConfig.countryCode);
        if(!country) {
            this.services.logger.error(`Missing country code "${this.stationConfig.countryCode}" for station ${this.stationCode}`);
        }
        return country;
    }

    get countryCode(): string {
        return this.country?.code || '';
    }

    get countryName(): string {
        return this.country?.name || '';
    }

    get markets(): Station[] {
        const markets: Station[] = []
        this.marketsConfig.forEach(m => {
            let station = this.services.stations.tryGetStation(m.code);
            if(station) {
                markets.push(station);
                if(station.mac) {
                    station = this.services.stations.tryGetStation(station.mac.code);
                    if(station) {
                        markets.push(station);
                    }
                }
            }
        });
        return markets.distinct(m => m.stationCode);
    }

    isOnlineCheckInAllowed(destination: Station): boolean {
        const market = this.marketsConfig.find(m => m.code === destination.stationCode);
        if(market) {
            return market.allowedOnlineCheckIn;
        }

        return false;
    }
}

export type NullableStation = Station | null;
export type NullableUndefinedStation = Station | null | undefined;

export interface PsoMarketDetectionOptions {
    bookingDate: Date;
    departureDate?: NullableDate;
}

export interface IStationService {
    getAllStations(): Station[];
    getAllStationsWithoutMacs(): Station[];
    findStations(stationCodes: string[]): Station[];
    getStation(stationCode: string): Station;
    tryGetStation(stationCode: NullableUndefinedString): NullableStation;
    stationExists(stationCode: string): boolean;
    groupStationsAlphabetically(stations: Station[]): Record<string, Station[]>;
    getPsoMarket(from: NullableStation, to: NullableStation, options: PsoMarketDetectionOptions): IPsoMarket | null;
    getPossiblePsoMarkets(from: NullableStation, to: NullableStation): IPsoMarket[];
    isStandByRoute(from: NullableStation, to: NullableStation): boolean;
}


